import { useState, useEffect, useRef, useCallback } from 'react';

const SCROLL_THRESHOLD = 10;
const SCROLL_THRESHOLD_BEFORE_HIDE = 200;
const MOBILE_BREAKPOINT = 768;

export interface UseScrollDirectionResult {
  direction: 'up' | 'down' | null;
  isMobile: boolean;
  /** true si scrollY >= SCROLL_THRESHOLD_BEFORE_HIDE — el menú puede ocultarse al hacer scroll down */
  hasScrolledPastHideThreshold: boolean;
  /** Fuerza direction='up' — llamar al volver de un reto/recurso para mostrar la barra */
  reset: () => void;
}

/**
 * Returns scroll direction and mobile flag.
 * Only on mobile (max-width 768px). On desktop direction is always 'up'.
 * Used to show/hide mobile nav on scroll.
 */
export function useScrollDirection(): UseScrollDirectionResult {
  const [direction, setDirection] = useState<'up' | 'down' | null>(null);
  // Inicialización síncrona vía matchMedia: si no lo hacemos aquí, el
  // primer render usa false (desktop) y aparece la barra desktop por un
  // frame antes de que el useEffect detecte mobile y cambie de branch —
  // ese era el flash visible al refrescar en pantallas pequeñas.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
  });
  const [hasScrolledPastHideThreshold, setHasScrolledPastHideThreshold] = useState(false);
  const lastScrollY = useRef(0);
  const lockedUntilRef = useRef(0);

  const resetTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const reset = useCallback(() => {
    setDirection('up');
    setHasScrolledPastHideThreshold(false);
    lastScrollY.current = window.scrollY;
    // Lock must outlast: holding (1600ms) + reveal (400ms) + scroll restoration (~200ms) = ~2200ms.
    // Using 2500ms for safety margin.
    lockedUntilRef.current = Date.now() + 2500;

    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      lockedUntilRef.current = 0;
      lastScrollY.current = window.scrollY + SCROLL_THRESHOLD + 1;
      window.dispatchEvent(new Event('scroll'));
    }, 2500);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    setIsMobile(mq.matches);

    const handleResize = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handleResize);
    return () => mq.removeEventListener('change', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setDirection('up');
      setHasScrolledPastHideThreshold(false);
      return;
    }

    const handleScroll = () => {
      if (Date.now() < lockedUntilRef.current) return;
      const currentScrollY = window.scrollY;
      const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;
      const isAtBottom = maxScrollY > 0 && maxScrollY - currentScrollY < 80;

      setHasScrolledPastHideThreshold(currentScrollY >= SCROLL_THRESHOLD_BEFORE_HIDE);

      if (isAtBottom) {
        setDirection('up');
        lastScrollY.current = currentScrollY;
      } else if (currentScrollY <= 0) {
        setDirection(null);
      } else if (currentScrollY > lastScrollY.current + SCROLL_THRESHOLD) {
        // Saltos grandes = restauración programática de scroll (no scroll del usuario)
        const delta = currentScrollY - lastScrollY.current;
        if (delta < SCROLL_THRESHOLD_BEFORE_HIDE) {
          setDirection('down');
        }
        lastScrollY.current = currentScrollY;
      } else if (currentScrollY < lastScrollY.current - SCROLL_THRESHOLD) {
        setDirection('up');
        lastScrollY.current = currentScrollY;
      }
    };

    lastScrollY.current = window.scrollY; // Evita detectar scroll down al montar con scroll restaurado
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  useEffect(() => {
    return () => clearTimeout(resetTimerRef.current);
  }, []);

  return { direction, isMobile, hasScrolledPastHideThreshold, reset };
}
